import { startOfDay, subDays } from 'date-fns'
import type { DayRecord } from '../types'

/** Deterministic pseudo-random so the prototype shows the same history every load. */
function seeded(seed: number): () => number {
  let s = seed
  return () => {
    s = (s * 1664525 + 1013904223) % 4294967296
    return s / 4294967296
  }
}

/**
 * Builds the last `days` days of Life Score history, excluding today.
 * The most recent 12 days stay at 70+ so they match Ana's active streak.
 */
export function buildHistory(days = 90, streak = 12): DayRecord[] {
  const next = seeded(20260921)
  const today = startOfDay(new Date())
  const out: DayRecord[] = []

  for (let i = days; i >= 1; i--) {
    const date = subDays(today, i)
    const roll = next()
    let score: number

    if (i <= streak) {
      score = 72 + Math.round(next() * 28)
    } else if (roll < 0.11) {
      score = 0
    } else if (roll < 0.3) {
      score = 42 + Math.round(next() * 24)
    } else {
      score = 68 + Math.round(next() * 32)
    }

    out.push({ date, score: Math.min(100, score) })
  }

  return out
}

export function average(records: DayRecord[]): number {
  if (records.length === 0) return 0
  const sum = records.reduce((acc, r) => acc + r.score, 0)
  return Math.round(sum / records.length)
}

export function perfectDays(records: DayRecord[]): number {
  return records.filter((r) => r.score === 100).length
}

/** Groups records into Monday-first week columns for the consistency map. */
export function toWeekColumns(records: DayRecord[]): (DayRecord | null)[][] {
  const columns: (DayRecord | null)[][] = []
  let column: (DayRecord | null)[] = []

  records.forEach((record, index) => {
    // date-fns getDay: 0 = Sunday. Monday-first index:
    const weekday = (record.date.getDay() + 6) % 7
    if (index === 0 && weekday > 0) {
      column = Array.from({ length: weekday }, () => null)
    }
    column.push(record)
    if (weekday === 6) {
      columns.push(column)
      column = []
    }
  })

  if (column.length > 0) {
    while (column.length < 7) column.push(null)
    columns.push(column)
  }

  return columns
}
